import React, { useRef, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { DashboardRow } from '../../../types/gotypes'
import {
  CellRef,
  MIN_CELL_WIDTH_PCT,
  STACK_BREAKPOINT,
  redistributeWidths,
} from '../../../utils/dashboardBuilderLayout'
import { calculateCellWidths } from '../../../utils/dashboard'
import LayoutDropZone from '../layout/LayoutDropZone'
import CellBoundaryHandle from '../layout/CellBoundaryHandle'
import BuilderCanvasCell from './BuilderCanvasCell'

interface BuilderCanvasRowProps {
  row: DashboardRow
  rowIndex: number
  onDuplicateCell: (ref: CellRef) => void
  onDeleteCell: (ref: CellRef) => void
  onDeleteRow: (rowIndex: number) => void
  /** Boundary drag (#1049) — moves width from cell `boundaryIndex` to the next. */
  onResizeBoundary: (rowIndex: number, boundaryIndex: number, deltaPct: number) => void
  onDistributeEvenly: (rowIndex: number) => void
}

/**
 * One row of the canvas. A row IS what makes cells sit side by side — the
 * user never sees the word "row"; they just see widgets landing next to each
 * other. `cell-gap` drop zones sit before, between, and after every cell so a
 * widget dropped there becomes a new column in this row (see
 * `dashboardBuilderLayout.insertWidgetAtDropTarget`); dropping past the last
 * gap when the row is full silently spills into a new row below, so no gap
 * needs to explain "this row is full" to the user.
 *
 * The boundary between two cells carries a drag handle that redistributes
 * their widths (#1049), writing the same `display_props.width` the dashboard
 * editor writes — the builder and the editor share both the handle and the
 * layout maths behind it.
 */
const BuilderCanvasRow: React.FC<BuilderCanvasRowProps> = ({
  row,
  rowIndex,
  onDuplicateCell,
  onDeleteCell,
  onDeleteRow,
  onResizeBoundary,
  onDistributeEvenly,
}) => {
  // #1116: no cell cap — every boundary accepts a drop.
  const cellsRef = useRef<HTMLDivElement | null>(null)
  // Live widths during a boundary drag. Null between drags, so the committed
  // layout stays the single source of truth rather than being mirrored here.
  const [previewWidths, setPreviewWidths] = useState<number[] | null>(null)
  const widths = previewWidths || calculateCellWidths(row.cells)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        position: 'relative',
        '&:hover .row-delete': { opacity: 1 },
      }}
    >
      <Box
        ref={cellsRef}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', [STACK_BREAKPOINT]: 'row' },
          alignItems: 'stretch',
          flex: 1,
          minWidth: 0,
        }}
      >
        <LayoutDropZone id={`cellgap-${rowIndex}-0`} orientation="column" target={{ kind: 'cell-gap', rowIndex, index: 0 }} />
        {row.cells.map((cell, cellIndex) => (
          <React.Fragment key={cellIndex}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                width: { xs: '100%', [STACK_BREAKPOINT]: `${widths[cellIndex]}%` },
                minWidth: 0,
                '&:hover [role="separator"]': { opacity: 1 },
              }}
            >
              <BuilderCanvasCell cell={cell} cellRef={{ rowIndex, cellIndex }} onDuplicate={onDuplicateCell} onDelete={onDeleteCell} />
              {cellIndex < row.cells.length - 1 && (
                <CellBoundaryHandle
                  getRowWidth={() => cellsRef.current?.clientWidth || 0}
                  onPreview={(deltaPct) => setPreviewWidths(
                    redistributeWidths(calculateCellWidths(row.cells), cellIndex, deltaPct, MIN_CELL_WIDTH_PCT)
                  )}
                  onCommit={(deltaPct) => {
                    setPreviewWidths(null)
                    onResizeBoundary(rowIndex, cellIndex, deltaPct)
                  }}
                  onDistributeEvenly={() => {
                    setPreviewWidths(null)
                    onDistributeEvenly(rowIndex)
                  }}
                />
              )}
            </Box>
            {(
              <LayoutDropZone
                id={`cellgap-${rowIndex}-${cellIndex + 1}`}
                orientation="column"
                target={{ kind: 'cell-gap', rowIndex, index: cellIndex + 1 }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
      <Tooltip title="Delete row">
        <IconButton
          className="row-delete"
          size="small"
          onClick={() => onDeleteRow(rowIndex)}
          sx={{ opacity: 0, transition: 'opacity 120ms ease', ml: 0.5, alignSelf: 'center' }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default BuilderCanvasRow
